import { Ballot, EventSettings, Project, Criterion, ScheduleItem } from '../types';

export interface RemoteConfig {
  settings?: EventSettings;
  projects?: Project[];
  criteria?: Criterion[];
  schedule?: ScheduleItem[];
  updatedAt?: string;
}

export async function fetchRemoteConfig(): Promise<RemoteConfig | null> {
  try {
    const res = await fetch('/api/config', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.success && data.config) {
      return {
        settings: data.config.settings,
        projects: data.config.projects,
        criteria: data.config.criteria,
        schedule: data.config.schedule,
        updatedAt: data.updatedAt,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export async function updateRemoteConfig(
  key: 'settings' | 'projects' | 'criteria' | 'schedule',
  value: any,
  adminPin: string = '1616'
): Promise<{ success: boolean; message?: string }> {
  try {
    const res = await fetch('/api/config', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-admin-pin': adminPin,
      },
      body: JSON.stringify({ key, value, adminPin }),
    });

    const data = await res.json();
    if (!res.ok || !data.success) {
      return { success: false, message: data.message || data.error || 'Failed to update config' };
    }
    return { success: true };
  } catch (e: any) {
    return { success: false, message: e.message || 'Network error updating config' };
  }
}

export async function updateBatchRemoteConfig(
  config: Partial<RemoteConfig>,
  adminPin: string = '1616'
): Promise<{ success: boolean; message?: string }> {
  try {
    const res = await fetch('/api/config', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-admin-pin': adminPin,
      },
      body: JSON.stringify({ config, adminPin }),
    });

    const data = await res.json();
    if (!res.ok || !data.success) {
      return { success: false, message: data.message || data.error || 'Failed to update config batch' };
    }
    return { success: true };
  } catch (e: any) {
    return { success: false, message: e.message || 'Network error updating config batch' };
  }
}

export async function fetchRemoteBallots(): Promise<Ballot[] | null> {
  try {
    const res = await fetch('/api/ballots', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.success && Array.isArray(data.ballots)) {
      return data.ballots;
    }
    return null;
  } catch {
    // Network / offline fallback
    return null;
  }
}

export async function submitRemoteBallot(ballot: Ballot): Promise<{ success: boolean; message?: string; ballot?: Ballot }> {
  try {
    const res = await fetch('/api/ballots', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ ballot }),
    });

    const data = await res.json();
    if (!res.ok || !data.success) {
      return { success: false, message: data.message || data.error || 'Submission failed' };
    }
    return { success: true, ballot: data.ballot };
  } catch (e: any) {
    return { success: false, message: e.message || 'Network error submitting ballot' };
  }
}

export async function submitBatchRemoteBallots(ballots: Ballot[]): Promise<{ success: boolean; count?: number }> {
  try {
    const res = await fetch('/api/ballots', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ ballots }),
    });

    const data = await res.json();
    return { success: !!data.success, count: data.count };
  } catch {
    return { success: false };
  }
}

export async function resetRemoteBallots(pin: string): Promise<boolean> {
  try {
    const res = await fetch('/api/ballots/reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin }),
    });
    const data = await res.json();
    return !!data.success;
  } catch {
    return false;
  }
}
